import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const TITLE_OPTIONS = ['', 'Caregiver', 'Executive Director', 'Maintenance Director', 'Nurse', 'Other'];

/**
 * UserDrawer — slide-out drawer for Add / Edit user.
 * Includes role-weight authorization, group picker, and password reset section.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   user: object | null,                // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (user: object) => void,
 *   onResetPassword: (data: object) => void,
 *   roles: Array<{ user_role_id: number, user_role_name: string, user_role_weight: number }>,
 *   groups: Array<{ group_id: number, group_name: string, capcode: string }>,
 *   myRoleWeight: number,
 *   myUsername: string,
 *   displayEditAllowed: boolean,
 *   iAmHigherAuthority: (comparedWeight: number, myWeight: number) => boolean
 * }} props
 */
const UserDrawer = ({
  isOpen, onClose, user, onSave, onDelete, onResetPassword,
  roles, groups, myRoleWeight, myUsername, displayEditAllowed,
  iAmHigherAuthority
}) => {
  const isEditMode = !!user;
  const isSelfEdit = isEditMode && user?.username === myUsername;
  const drawerRef = useRef(null);

  // Form state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [title, setTitle] = useState('');
  const [titleCustom, setTitleCustom] = useState('');
  const [roleId, setRoleId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Password reset state (edit mode only)
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Populate form on open/user change
  useEffect(() => {
    if (isOpen && user) {
      setUsername(user.username || '');
      setDisplayName(user.display_name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setPassword('');
      setConfirmPassword('');
      setTitle(
        TITLE_OPTIONS.includes(user.title) ? (user.title || '') : 'Other'
      );
      setTitleCustom(
        TITLE_OPTIONS.includes(user.title) ? '' : (user.title || '')
      );
      setRoleId(user.user_role_id?.toString() || '');
      setSelectedGroupIds(
        (user.group_ids || []).map((id) => id.toString())
      );
      setGroupSearch('');
      setShowDeleteConfirm(false);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmNewPassword('');
    } else if (isOpen && !user) {
      setUsername('');
      setDisplayName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
      setTitle('');
      setTitleCustom('');
      setRoleId('');
      setSelectedGroupIds([]);
      setGroupSearch('');
      setShowDeleteConfirm(false);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmNewPassword('');
    }
  }, [isOpen, user]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Filtered roles: only show roles the current user has authority over
  const filteredRoles = useMemo(() => {
    if (!roles) return [];
    return roles.filter((role) => iAmHigherAuthority(role.user_role_weight, myRoleWeight));
  }, [roles, myRoleWeight, iAmHigherAuthority]);

  // Determine if selected role is caregiver (email not required for caregivers)
  const selectedRole = useMemo(() => {
    if (!roleId || !roles) return null;
    return roles.find((r) => r.user_role_id.toString() === roleId) || null;
  }, [roleId, roles]);

  const isCaregiver = selectedRole?.user_role_name?.toLowerCase() === 'caregiver';

  // Filter groups by search
  const filteredGroups = useMemo(() => {
    if (!groupSearch.trim()) return groups || [];
    const s = groupSearch.toLowerCase();
    return (groups || []).filter(
      (g) =>
        g.group_name?.toLowerCase().includes(s) ||
        g.capcode?.toLowerCase().includes(s)
    );
  }, [groups, groupSearch]);

  const handleGroupToggle = useCallback((groupId) => {
    const gStr = groupId.toString();
    setSelectedGroupIds((prev) =>
      prev.includes(gStr) ? prev.filter((id) => id !== gStr) : [...prev, gStr]
    );
  }, []);

  const handleSelectAllGroups = useCallback(() => {
    const allFilteredIds = filteredGroups.map((g) => g.group_id.toString());
    setSelectedGroupIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
  }, [filteredGroups]);

  const handleClearGroups = useCallback(() => {
    setSelectedGroupIds([]);
  }, []);

  // Check if admin role (role_id 1 or 2) is using weak password
  const isAdminWeakPassword = useMemo(() => {
    const rid = parseInt(roleId, 10);
    if (rid !== 1 && rid !== 2) return false;
    if (!isEditMode && password === '123456') return true;
    return false;
  }, [roleId, password, isEditMode]);

  // Form validation
  const isFormValid = useMemo(() => {
    if (isEditMode) {
      return displayName.trim() && roleId;
    }
    // Add mode
    if (!username.trim()) return false;
    if (!displayName.trim()) return false;
    if (!password || password.length < 6) return false;
    if (password !== confirmPassword) return false;
    if (!roleId) return false;
    if (!isCaregiver && !email.trim()) return false;
    if (isAdminWeakPassword) return false;
    return true;
  }, [isEditMode, username, displayName, email, password, confirmPassword, roleId, isCaregiver, isAdminWeakPassword]);

  // Password reset validation
  const isPasswordResetValid = useMemo(() => {
    if (!newPassword || newPassword.length < 6) return false;
    if (newPassword !== confirmNewPassword) return false;
    // Admin weak password check for reset
    const rid = parseInt(roleId, 10);
    if ((rid === 1 || rid === 2) && newPassword === '123456') return false;
    return true;
  }, [newPassword, confirmNewPassword, roleId]);

  // Can current user reset password for this user?
  const canResetPassword = useMemo(() => {
    if (!isEditMode) return false;
    if (isSelfEdit) return true;
    return iAmHigherAuthority(user?.role_weight, myRoleWeight);
  }, [isEditMode, isSelfEdit, user, myRoleWeight, iAmHigherAuthority]);

  // Can current user delete this user?
  const canDelete = useMemo(() => {
    if (!isEditMode || !user) return false;
    if (isSelfEdit) return false;
    return iAmHigherAuthority(user?.role_weight, myRoleWeight);
  }, [isEditMode, user, isSelfEdit, myRoleWeight, iAmHigherAuthority]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setSaving(true);

      const formData = {
        user_id: user?.user_id || null,
        display_name: displayName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.replace(/-/g, ''),
        user_role_id: parseInt(roleId, 10),
        group_ids: selectedGroupIds.map((id) => parseInt(id, 10)),
        title: title === 'Other' ? titleCustom.trim() : title,
        cognito_id: user?.cognito_id,
      };

      // Only include username and password for add mode
      if (!isEditMode) {
        formData.username = username.trim();
        formData.password = password;
      }

      setTimeout(() => {
        onSave(formData);
        setSaving(false);
      }, 400);
    },
    [user, username, displayName, email, phone, password, roleId, selectedGroupIds, title, titleCustom, isEditMode, onSave]
  );

  const handlePasswordReset = useCallback(() => {
    if (!isPasswordResetValid || !user) return;
    setSavingPassword(true);

    setTimeout(() => {
      onResetPassword({
        user_id: user.user_id,
        username: user.username,
        password: newPassword,
      });
      setSavingPassword(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordReset(false);
    }, 400);
  }, [isPasswordResetValid, user, newPassword, onResetPassword]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (user && onDelete) {
      onDelete(user);
    }
    setShowDeleteConfirm(false);
  }, [user, onDelete]);

  return (
    <>
      {/* Backdrop */}
      {isOpen ? (
        <div className="up-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      ) : null}

      {/* Drawer panel */}
      <div
        ref={drawerRef}
        className={`up-drawer up-drawer--lg ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={isEditMode ? 'Edit User' : 'New User'}
      >
        {/* Header */}
        <div className="up-drawer__header">
          <h2 className="up-drawer__title">
            {isEditMode ? 'Edit User' : 'New User'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="up-drawer__close"
            aria-label="Close drawer"
          >
            <svg className="up-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <form onSubmit={handleSubmit} className="up-form">
          <div className="up-form__body">
            {/* Username (add mode only) */}
            {!isEditMode ? (
              <div>
                <label htmlFor="drawer-username" className="up-field__label">
                  Username <span className="up-field__required">*</span>
                </label>
                <input
                  type="text"
                  id="drawer-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  pattern="^[a-zA-Z0-9.\-_@]+$"
                  className="up-field__input"
                  placeholder="Enter username"
                />
              </div>
            ) : null}

            {/* Display Name */}
            <div>
              <label htmlFor="drawer-display-name" className="up-field__label">
                Display Name <span className="up-field__required">*</span>
              </label>
              <input
                type="text"
                id="drawer-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                disabled={isSelfEdit && !displayEditAllowed}
                className="up-field__input"
                placeholder="Enter display name"
              />
              {isSelfEdit && !displayEditAllowed ? (
                <p className="up-field__hint">Display name editing is not enabled for your account.</p>
              ) : null}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="drawer-email" className="up-field__label">
                Email {!isCaregiver ? <span className="up-field__required">*</span> : null}
              </label>
              <input
                type="email"
                id="drawer-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={!isCaregiver}
                className="up-field__input"
                placeholder="email@example.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="drawer-phone" className="up-field__label">
                Phone
              </label>
              <input
                type="tel"
                id="drawer-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                pattern="[0-9]{3}-?[0-9]{3}-?[0-9]{4}"
                className="up-field__input"
                placeholder="555-555-5555"
              />
            </div>

            {/* Password fields (add mode only) */}
            {!isEditMode ? (
              <>
                <div>
                  <label htmlFor="drawer-password" className="up-field__label">
                    Password <span className="up-field__required">*</span>
                  </label>
                  <input
                    type="password"
                    id="drawer-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="up-field__input"
                    placeholder="Minimum 6 characters"
                  />
                  {isAdminWeakPassword ? (
                    <p className="up-field__error">Admin roles cannot use "123456" as a password.</p>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="drawer-confirm-password" className="up-field__label">
                    Confirm Password <span className="up-field__required">*</span>
                  </label>
                  <input
                    type="password"
                    id="drawer-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="up-field__input"
                    placeholder="Re-enter password"
                  />
                  {confirmPassword && password !== confirmPassword ? (
                    <p className="up-field__error">Passwords do not match.</p>
                  ) : null}
                </div>
              </>
            ) : null}

            {/* Title */}
            <div>
              <label htmlFor="drawer-title" className="up-field__label">
                Title
              </label>
              <select
                id="drawer-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (e.target.value !== 'Other') setTitleCustom('');
                }}
                className="up-field__select"
              >
                {TITLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || 'Select title...'}
                  </option>
                ))}
              </select>
              {title === 'Other' ? (
                <input
                  type="text"
                  id="drawer-title-custom"
                  value={titleCustom}
                  onChange={(e) => setTitleCustom(e.target.value)}
                  className="up-field__input"
                  placeholder="Enter custom title"
                  style={{ marginTop: '0.5rem' }}
                />
              ) : null}
            </div>

            {/* User Role */}
            <div>
              <label htmlFor="drawer-role" className="up-field__label">
                User Role <span className="up-field__required">*</span>
              </label>
              <select
                id="drawer-role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                required
                disabled={isSelfEdit}
                className="up-field__select"
              >
                <option value="">Select role...</option>
                {filteredRoles.map((role) => (
                  <option key={role.user_role_id} value={role.user_role_id}>
                    {role.user_role_name}
                  </option>
                ))}
              </select>
              {isSelfEdit ? (
                <p className="up-field__hint">You cannot change your own role.</p>
              ) : null}
            </div>

            {/* Group Picker */}
            <div className="up-user-picker">
              <div className="up-user-picker__header">
                <p className="up-field__label" style={{ marginBottom: 0 }}>
                  Groups
                  <span className="up-user-picker__count">
                    ({selectedGroupIds.length} selected)
                  </span>
                </p>
              </div>

              {/* Search */}
              <div className="up-user-picker__search">
                <i className="fe fe-search up-user-picker__search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  className="up-user-picker__search-input"
                />
              </div>

              {/* Select All / Clear */}
              <div className="up-user-picker__actions">
                <button
                  type="button"
                  onClick={handleSelectAllGroups}
                  className="up-user-picker__action-btn"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClearGroups}
                  className="up-user-picker__action-btn"
                >
                  Clear
                </button>
              </div>

              {/* Group list */}
              <div className="up-user-picker__list">
                {filteredGroups.length === 0 ? (
                  <p className="up-user-picker__empty">No groups found</p>
                ) : (
                  filteredGroups.map((g) => {
                    const isChecked = selectedGroupIds.includes(g.group_id.toString());
                    return (
                      <label
                        key={g.group_id}
                        className={`up-user-picker__item ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleGroupToggle(g.group_id)}
                          className="up-checkbox"
                        />
                        <div className="up-user-picker__item-info">
                          <span className="up-user-picker__item-name">{g.group_name}</span>
                          <span className="up-user-picker__item-type">
                            {g.capcode || ''}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Password Reset Section (edit mode only) */}
            {isEditMode && canResetPassword ? (
              <div className="up-password-section">
                <div className="up-password-section__divider" />
                <h3 className="up-password-section__title">Password Reset</h3>

                {!showPasswordReset ? (
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(true)}
                    className="up-btn up-btn--outline"
                  >
                    <i className="fe fe-lock" aria-hidden="true" />
                    Change Password
                  </button>
                ) : (
                  <>
                    <div>
                      <label htmlFor="drawer-new-password" className="up-field__label">
                        New Password <span className="up-field__required">*</span>
                      </label>
                      <input
                        type="password"
                        id="drawer-new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        minLength={6}
                        className="up-field__input"
                        placeholder="Minimum 6 characters"
                      />
                    </div>
                    <div>
                      <label htmlFor="drawer-confirm-new-password" className="up-field__label">
                        Confirm New Password <span className="up-field__required">*</span>
                      </label>
                      <input
                        type="password"
                        id="drawer-confirm-new-password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        minLength={6}
                        className="up-field__input"
                        placeholder="Re-enter new password"
                      />
                      {confirmNewPassword && newPassword !== confirmNewPassword ? (
                        <p className="up-field__error">Passwords do not match.</p>
                      ) : null}
                    </div>
                    <div className="up-password-section__actions">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPasswordReset(false);
                          setNewPassword('');
                          setConfirmNewPassword('');
                        }}
                        className="up-btn up-btn--cancel up-btn--sm"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handlePasswordReset}
                        disabled={!isPasswordResetValid || savingPassword}
                        className="up-btn up-btn--primary up-btn--sm"
                      >
                        {savingPassword ? (
                          <span className="up-saving-text">
                            <svg className="up-icon-md up-spinner" fill="none" viewBox="0 0 24 24">
                              <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Resetting...
                          </span>
                        ) : (
                          'Reset Password'
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer with actions */}
          <div className="up-form__footer">
            {/* Delete button (edit mode only, if authorized) */}
            {isEditMode && canDelete && !showDeleteConfirm ? (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="up-btn up-btn--danger"
              >
                <i className="fe fe-trash-2" aria-hidden="true" />
                Delete
              </button>
            ) : null}

            {/* Delete confirmation */}
            {isEditMode && canDelete && showDeleteConfirm ? (
              <div className="up-delete-confirm">
                <span className="up-delete-confirm__text">
                  Delete <strong>{user?.username}</strong>?
                </span>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="up-btn up-btn--cancel up-btn--sm"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="up-btn up-btn--danger-solid up-btn--sm"
                >
                  Delete
                </button>
              </div>
            ) : null}

            {/* Spacer pushes Cancel/Save right */}
            {!showDeleteConfirm ? <div style={{ flex: 1 }} /> : null}

            {!showDeleteConfirm ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="up-btn up-btn--cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || saving}
                  className="up-btn up-btn--primary"
                >
                  {saving ? (
                    <span className="up-saving-text">
                      <svg className="up-icon-md up-spinner" fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    isEditMode ? 'Save Changes' : 'Create User'
                  )}
                </button>
              </>
            ) : null}
          </div>
        </form>
      </div>
    </>
  );
};

export default UserDrawer;
